import {
  UserOrderInfo,
  V3_POOL_SPLITTER,
  pointToPrice,
} from '../../../services/swapV3';
import { TokenMetadata } from '../../../services/ft-contract';
import React, { useState, useContext, useMemo, Fragment, useRef } from 'react';
import { getTxId } from '../../../services/indexer';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  toReadableNumber,
  scientificNotationToString,
  checkAllocations,
  calculateFeePercent,
  ONLY_ZEROS,
  toPrecision,
} from '../../../utils/numbers';
import Big from 'big.js';
import { toRealSymbol } from '../../../utils/token';
import BigNumber from 'bignumber.js';
import QuestionMark from '../../farm/QuestionMark';
import CustomTooltip from 'src/components/customTooltip/customTooltip';
import moment from 'moment';
import { TIMESTAMP_DIVISOR } from 'src/constants/timestampDivisor';
import { HiOutlineExternalLink, BsCheckCircle } from '../../reactIcons/index';
import getConfig from '../../../services/config';
import {
  NearblocksIcon,
  PikespeakIcon,
  TxLeftArrow,
} from 'src/components/icon/Pool';
import { PriceContext, MobileInfoBanner } from './myOrderShared';
import { ExclamationTip } from '../../layout/TipWrapper';
import { isClientMobile } from '../../../utils/device';
import {
  MyOrderInstantSwapArrowRight,
  MobileHistoryOrderStamp,
} from '../../icon/swapV3';
import { MyOrderMobileArrow } from '../../icon/Arrows';
import { TOKEN_LIST_FOR_RATE } from '../../../services/dcl/dclDisplay';

export function HistoryLine({
  order,
  index,
  tokensMap,
  sellAmountToBuyAmount,
  orderTx,
  hoverOn,
  setHoverOn,
}: {
  order: UserOrderInfo;
  index: number;
  tokensMap: { [key: string]: TokenMetadata };
  sellAmountToBuyAmount: any;
  orderTx: string;
  hoverOn: number;
  setHoverOn: React.Dispatch<React.SetStateAction<number>>;
}) {
  const [loadingStates, setLoadingStates] = useState({});
  const [hoveredTx, setHoveredTx] = useState(null);
  const closeTimeoutRef = useRef(null);

  const handleMouseEnter = (receipt_id) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setHoveredTx(receipt_id);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredTx(null);
    }, 200);
  };

  async function handleTxClick(receipt_id, url) {
    setLoadingStates((prevStates) => ({ ...prevStates, [receipt_id]: true }));
    try {
      const txIdResult = await getTxId(receipt_id);
      if (txIdResult && txIdResult.receipts && txIdResult.receipts.length > 0) {
        const txHash = txIdResult.receipts[0].originated_from_transaction_hash;
        window.open(`${url}/${txHash}`, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error(
        'An error occurred while fetching transaction data:',
        error
      );
    } finally {
      setLoadingStates((prevStates) => ({
        ...prevStates,
        [receipt_id]: false,
      }));
    }
  }
  const intl = useIntl();
  const buyToken = tokensMap[order.buy_token];
  const sellToken = tokensMap[order.sell_token];
  if (!buyToken || !sellToken) return null;

  const swapIn = toReadableNumber(
    sellToken.decimals,
    scientificNotationToString(
      new Big(order.original_deposit_amount || '0')
        .minus(order.original_amount || '0')
        .toString()
    )
  );

  const swapOut = toReadableNumber(
    buyToken.decimals,
    order.swap_earn_amount || '0'
  );

  const orderIn = toReadableNumber(
    sellToken.decimals,
    order.original_amount || '0'
  );

  const totalIn = toReadableNumber(
    sellToken.decimals,
    order.original_deposit_amount || '0'
  );

  const calPoint =
    sellToken.id === order.pool_id.split(V3_POOL_SPLITTER)[0]
      ? order.point
      : -order.point;

  const price = pointToPrice({
    tokenA: sellToken,
    tokenB: buyToken,
    point: calPoint,
  });

  const buyAmountRaw = sellAmountToBuyAmount(
    order.original_amount,
    order,
    price
  );

  const buyAmount = new Big(buyAmountRaw).gt(
    toReadableNumber(buyToken.decimals, order.bought_amount || '0')
  )
    ? buyAmountRaw
    : toReadableNumber(buyToken.decimals, order.bought_amount || '0');

  const totalOut = scientificNotationToString(
    new Big(buyAmount).plus(swapOut).toString()
  );

  const claimedAmount = toReadableNumber(
    buyToken.decimals,
    order.bought_amount || '0'
  );

  const buyAmountToSellAmount = (
    undecimaled_amount: string,
    order: UserOrderInfo,
    price: string
  ) => {
    const sell_amount = new Big(
      toReadableNumber(
        tokensMap[order.buy_token].decimals,
        undecimaled_amount || '0'
      )
    )
      .div(price)
      .toString();
    return scientificNotationToString(sell_amount);
  };

  const claimedAmountIn = buyAmountToSellAmount(
    scientificNotationToString(
      new Big(order.bought_amount || '0')
        .minus(order.unclaimed_amount || '0')
        .toString()
    ),
    order,
    price
  );

  const cancelAmount = sellAmountToBuyAmount(order.cancel_amount, order, price);

  const amountTotal = new Big(claimedAmount || '0').plus(cancelAmount || '0');

  const pClaimedAmount = new Big(claimedAmount || '0')
    .div(amountTotal.lte(0) ? 1 : amountTotal)
    .times(100)
    .toNumber();

  const pCancelAmount = new Big(cancelAmount || '0')
    .div(amountTotal.lte(0) ? 1 : amountTotal)
    .times(100)
    .toNumber();

  const displayPercents = checkAllocations('100', [
    pClaimedAmount > 0 && pClaimedAmount < 5
      ? '5'
      : scientificNotationToString(pClaimedAmount.toString()),
    pCancelAmount > 0 && pCancelAmount < 5
      ? '5'
      : scientificNotationToString(pCancelAmount.toString()),
  ]);

  const getClaimAmountTip = () => {
    return `
      <div 
        class="flex flex-col text-xs min-w-36 text-farmText z-50"
      >
      ${
        ONLY_ZEROS.test(claimedAmount)
          ? ''
          : `
      <div class="flex items-center justify-between my-1">
          <span class="flex items-center mr-1">
              <div class="w-1.5 h-1.5 rounded-full bg-gradientFrom mr-1">
              </div>

              ${intl.formatMessage({
                id: 'claimed_upper',
                defaultMessage: 'Claimed',
              })}

          </span>

          <span>
          ${
            Number(claimedAmount) > 0 && Number(claimedAmount) < 0.001
              ? '< 0.001'
              : toPrecision(claimedAmount, 3)
          }
          </span>

      </div>
      `
      }


      ${
        ONLY_ZEROS.test(cancelAmount)
          ? ''
          : `<div class="flex items-center my-1 justify-between">
          <span class="flex items-center ">
              <div class="w-1.5 h-1.5 rounded-full bg-primaryText mr-1">
              </div>

              ${intl.formatMessage({
                id: 'canceled',
                defaultMessage: 'Canceled',
              })}

          </span>

          <span>
          ${
            Number(cancelAmount) > 0 && Number(cancelAmount) < 0.001
              ? '< 0.001'
              : toPrecision(cancelAmount, 3)
          }

          </span>

      </div>`
      }

      </div>
  `;
  };

  const sellTokenAmount = (
    <span className="flex py-4 pl-3  flex-shrink-0 items-center">
      <img
        src={sellToken.icon}
        className="border border-gradientFrom rounded-full w-7 h-7"
        alt=""
      />

      <div className="flex   xs:flex-row flex-col ml-2">
        <span className="text-white text-sm mr-2" title={orderIn}>
          {Number(orderIn) > 0 && Number(orderIn) < 0.01
            ? '< 0.01'
            : toPrecision(orderIn, 2)}
        </span>

        <span className="text-v3SwapGray text-xs xs:relative xs:top-0.5">
          {toRealSymbol(sellToken.symbol)}
        </span>
      </div>
    </span>
  );

  const buyTokenAmount = (
    <span className="flex items-center col-span-1 ">
      <img
        src={buyToken.icon}
        className="border flex-shrink-0 border-gradientFrom rounded-full w-7 h-7"
        alt=""
      />

      <div className="flex xs:flex-row flex-col ml-2">
        <span
          className="text-white mr-2 text-sm whitespace-nowrap"
          title={buyAmount}
        >
          {Number(buyAmount) > 0 && Number(buyAmount) < 0.01
            ? '< 0.01'
            : toPrecision(buyAmount, 2)}
        </span>

        <span className="text-v3SwapGray text-xs xs:relative xs:top-0.5">
          {toRealSymbol(buyToken.symbol)}
        </span>
      </div>
    </span>
  );

  const fee = Number(order.pool_id.split(V3_POOL_SPLITTER)[2]);

  const feeTier = (
    <span className="rounded-lg relative xsm:right-0 xsm:bg-none right-3 text-left  text-primaryText p-1 lg:bg-menuMoreBgColor xs:text-white">
      {`${toPrecision(calculateFeePercent(fee / 100).toString(), 2)}% `}
    </span>
  );
  const sort =
    TOKEN_LIST_FOR_RATE.indexOf(sellToken?.symbol) > -1 && +price !== 0;
  const orderRate = useMemo(() => {
    let displayPrice = price;
    if (sort) {
      displayPrice = new BigNumber(1).dividedBy(price).toFixed();
    }
    return (
      <span className="whitespace-nowrap col-span-1 flex items-start xs:flex-row xs:items-center flex-col relative xs:right-0">
        <span className="mr-1 text-white text-sm" title={displayPrice}>
          {toPrecision(displayPrice, 2)}
        </span>
        <span className="text-v3SwapGray text-xs xs:hidden">
          {`${toRealSymbol(
            sort ? sellToken.symbol : buyToken.symbol
          )}/${toRealSymbol(sort ? buyToken.symbol : sellToken.symbol)}`}
        </span>
        <span className="text-white text-sm lg:hidden md:hidden">
          {`${toRealSymbol(sort ? sellToken.symbol : buyToken.symbol)}`}
        </span>
      </span>
    );
  }, [price, buyToken, sellToken]);

  const claimTip = (
    <div
      className="text-xs relative xs:bottom-2 mt-1 mr-1 w-20 xs:w-full flex items-center xs:flex-row-reverse "
      data-type="info"
      data-place="bottom"
      data-multiline={true}
      data-class="reactTip"
      data-tooltip-html={getClaimAmountTip()}
      data-tooltip-id={'claim_tip_' + order.order_id}
    >
      <span className="mr-1 xs:ml-2">
        <QuestionMark color="dark" />
      </span>
      <div className="flex items-center w-full">
        {displayPercents.map((percent, percentIndex) => {
          if (ONLY_ZEROS.test(percent)) return null;

          const bgColor =
            percentIndex === 0 ? 'bg-gradientFrom' : 'bg-primaryText';

          return (
            <div
              className={`mx-px h-1 xs:h-2 rounded-lg ${bgColor}`}
              style={{
                width: percent + '%',
              }}
            />
          );
        })}
      </div>
      <CustomTooltip
        className="w-20"
        id={'claim_tip_' + order.order_id}
        place="bottom"
      />
    </div>
  );

  const claimed = (
    <span className="whitespace-nowrap  xs:flex-col flex items-center ">
      <div>
        <div className="flex items-center xs:justify-end">
          <img
            src={buyToken.icon}
            className="border border-gradientFrom rounded-full w-4 h-4"
            alt=""
          />
          <span
            className="text-white text-sm mx-1"
            title={toReadableNumber(
              buyToken.decimals,
              order.bought_amount || '0'
            )}
          >
            {Number(
              toReadableNumber(buyToken.decimals, order.bought_amount || '0')
            ) > 0 &&
            Number(
              toReadableNumber(buyToken.decimals, order.bought_amount || '0')
            ) < 0.001
              ? '< 0.001'
              : toPrecision(
                  toReadableNumber(
                    buyToken.decimals,
                    order.bought_amount || '0'
                  ),
                  3
                )}
          </span>
        </div>
        <div className="xs:hidden">{claimTip}</div>
      </div>
    </span>
  );

  const created = (
    <span className=" relative  whitespace-nowrap    text-primaryText xs:text-xs flex flex-col   xsm:justify-center  text-left xs:opacity-50">
      <span className="xsm:hidden">
        {moment(
          Math.floor(Number(order.created_at) / TIMESTAMP_DIVISOR) * 1000
        ).format('YYYY-MM-DD')}
      </span>
      <span className="xsm:hidden">
        {moment(
          Math.floor(Number(order.created_at) / TIMESTAMP_DIVISOR) * 1000
        ).format('HH:mm')}
      </span>

      <span className="lg:hidden text-center relative bottom-2">
        {moment(
          Math.floor(Number(order.created_at) / TIMESTAMP_DIVISOR) * 1000
        ).format('YYYY-MM-DD HH:mm')}
      </span>
    </span>
  );
  const actions = (
    <div className=" col-span-1  text-primaryText  text-xs flex flex-col items-end justify-self-end p-1.5 pr-4">
      <span className="flex text-sm text-white items-center whitespace-nowrap">
        {ONLY_ZEROS.test(order.cancel_amount) ? (
          <FormattedMessage id="filled" defaultMessage={'Filled'} />
        ) : new Big(order.cancel_amount).eq(order.original_deposit_amount) ? (
          <FormattedMessage id="canceled" defaultMessage={'Canceled'} />
        ) : (
          <div className="flex flex-col items-end">
            <span className="whitespace-nowrap mb-0.5">
              <FormattedMessage id="partially" defaultMessage={'Partially'} />
            </span>
            <span className="whitespace-nowrap">
              <FormattedMessage id="filled" defaultMessage={'Filled'} />
            </span>
          </div>
        )}
      </span>

      <div className="relative">
        {!!orderTx && (
          <a
            className="flex items-center text-v3SwapGray cursor-pointer"
            onMouseEnter={() => handleMouseEnter(orderTx)}
            onMouseLeave={handleMouseLeave}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            {loadingStates[orderTx] ? (
              <>
                Tx
                <span className="loading-dots"></span>
              </>
            ) : (
              <>
                Tx
                <span className="ml-1.5">
                  <HiOutlineExternalLink />
                </span>
              </>
            )}
            {hoveredTx === orderTx && (
              <div className="w-44 absolute top-6 left-0 bg-poolDetaileTxBgColor border border-poolDetaileTxBorderColor rounded-lg p-2 shadow-lg z-50">
                <div className="flex flex-col">
                  <div
                    className="mb-2 px-3 py-2 hover:bg-poolDetaileTxHoverColor text-white rounded-md flex items-center"
                    onMouseEnter={(event) => {
                      const arrow = event.currentTarget.querySelector(
                        '.arrow'
                      ) as HTMLElement;
                      if (arrow) {
                        arrow.style.display = 'block';
                      }
                    }}
                    onMouseLeave={(event) => {
                      const arrow = event.currentTarget.querySelector(
                        '.arrow'
                      ) as HTMLElement;
                      if (arrow) {
                        arrow.style.display = 'none';
                      }
                    }}
                    onClick={() =>
                      handleTxClick(orderTx, `${getConfig().explorerUrl}/txns`)
                    }
                  >
                    <NearblocksIcon />
                    <p className="ml-2 text-sm">nearblocks</p>
                    <div className="ml-3 arrow" style={{ display: 'none' }}>
                      <TxLeftArrow />
                    </div>
                  </div>
                  <div
                    className="px-3 py-2 hover:bg-poolDetaileTxHoverColor text-white rounded-md flex items-center"
                    onMouseEnter={(event) => {
                      const arrow = event.currentTarget.querySelector(
                        '.arrow'
                      ) as HTMLElement;
                      if (arrow) {
                        arrow.style.display = 'block';
                      }
                    }}
                    onMouseLeave={(event) => {
                      const arrow = event.currentTarget.querySelector(
                        '.arrow'
                      ) as HTMLElement;
                      if (arrow) {
                        arrow.style.display = 'none';
                      }
                    }}
                    onClick={() =>
                      handleTxClick(
                        orderTx,
                        `${getConfig().pikespeakUrl}/transaction-viewer`
                      )
                    }
                  >
                    <PikespeakIcon />
                    <p className="ml-2 text-sm">Pikespeak...</p>
                    <div className="ml-3 arrow" style={{ display: 'none' }}>
                      <TxLeftArrow />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </a>
        )}
      </div>
    </div>
  );

  const tokenPrice = useContext(PriceContext);

  const sellTokenPrice = tokenPrice?.[sellToken.id]?.price || null;
  const buyTokenPrice = tokenPrice?.[buyToken.id]?.price || null;
  function instant_swap_tip() {
    const token_sell_symbol = toRealSymbol(sellToken.symbol);
    const token_buy_symbol = toRealSymbol(buyToken.symbol);
    const sell_token_price = sellTokenPrice
      ? `($${toPrecision(sellTokenPrice, 2)})`
      : '';
    const buy_token_price = buyTokenPrice
      ? `($${toPrecision(buyTokenPrice, 2)})`
      : '';
    let rate = new Big(swapOut).div(ONLY_ZEROS.test(swapIn) ? 1 : swapIn);
    if (sort) {
      rate = new Big(1).div(rate.eq(0) ? '1' : rate);
    }
    const display_rate = rate.toFixed(3);
    let result = '';
    if (sort) {
      result = `1 ${token_buy_symbol} ${buy_token_price} = ${display_rate} ${token_sell_symbol}`;
    } else {
      result = `1 ${token_sell_symbol} ${sell_token_price} = ${display_rate} ${token_buy_symbol}`;
    }
    return result;
  }
  const swapBanner = (
    <>
      <td
        colSpan={8}
        className=" rounded-b-xl xsm:hidden  w-full relative bottom-1.5 pt-6 bg-portfolioBgColor"
      >
        {new Big(order.original_deposit_amount || '0')
          .minus(order.original_amount || '0')
          .gt(0) && (
          <>
            <div className="flex items-center px-4 pb-4 justify-between ">
              <span className="flex items-center">
                <FormattedMessage
                  id="initial_order"
                  defaultMessage={'Initial Order'}
                />
                <ExclamationTip
                  id="this_order_has_been_partially_filled"
                  defaultMessage="This order has been partially filled "
                  dataPlace="bottom"
                  colorhex="#7E8A93"
                  uniquenessId={
                    'this_order_has_been_partially_filled' + order.order_id
                  }
                />
              </span>

              <span className="flex items-center">
                <span title={totalIn} className="text-v3SwapGray">
                  {Number(totalIn) > 0 && Number(totalIn) < 0.01
                    ? '< 0.01'
                    : toPrecision(totalIn, 2)}
                </span>

                <span className="ml-1.5">{toRealSymbol(sellToken.symbol)}</span>
                <span className="mx-6 xs:mx-2 text-white xs:text-v3SwapGray">
                  {isClientMobile() ? (
                    <MyOrderInstantSwapArrowRight />
                  ) : (
                    <MyOrderInstantSwapArrowRight />
                  )}
                </span>
                <span
                  title={toPrecision(totalOut, buyToken.decimals)}
                  className="text-v3SwapGray"
                >
                  {Number(totalOut) > 0 && Number(totalOut) < 0.01
                    ? '< 0.01'
                    : toPrecision(totalOut, 2)}
                </span>

                <span className="ml-1.5">{toRealSymbol(buyToken.symbol)}</span>
              </span>
            </div>

            <div className="frcb px-4 pb-4">
              <span className="flex items-center ">
                <FormattedMessage
                  id="instants_swap"
                  defaultMessage={'Instant Swap'}
                />

                <ExclamationTip
                  colorhex="#7E8A93"
                  id={instant_swap_tip()}
                  defaultMessage={instant_swap_tip()}
                  dataPlace="bottom"
                  uniquenessId={'instant_swap_tip' + order.order_id}
                />
              </span>

              <span className="frcb min-w-p300">
                <div className="frcs text-xs w pr-2 text-v3SwapGray">
                  <BsCheckCircle
                    className="mr-1.5"
                    fill="#42bb17"
                    stroke="#42BB17"
                  />

                  <FormattedMessage
                    id="swappped"
                    defaultMessage={'Swapped'}
                  ></FormattedMessage>
                </div>

                <div className="flex items-center justify-end">
                  <span title={swapIn} className="text-v3SwapGray">
                    {Number(swapIn) > 0 && Number(swapIn) < 0.01
                      ? '< 0.01'
                      : toPrecision(swapIn, 2)}
                  </span>

                  <span className="ml-1.5">
                    {toRealSymbol(sellToken.symbol)}
                  </span>
                  <span className="mx-6 xs:mx-2 text-v3SwapGray">
                    {isClientMobile() ? (
                      <MyOrderInstantSwapArrowRight />
                    ) : (
                      <MyOrderInstantSwapArrowRight />
                    )}
                  </span>
                  <span title={swapOut} className="text-v3SwapGray">
                    {Number(swapOut) > 0 && Number(swapOut) < 0.01
                      ? '< 0.01'
                      : toPrecision(swapOut, 2)}
                  </span>

                  <span className="ml-1.5">
                    {toRealSymbol(buyToken.symbol)}
                  </span>
                </div>
              </span>
            </div>
          </>
        )}
        {Number(claimedAmountIn) > 0 && (
          <div className="frcb px-4 pb-4">
            <span>
              <FormattedMessage id="executed" defaultMessage={'Executed'} />
            </span>

            <span className="frcb min-w-p300">
              <div className="frcs text-xs pr-2 text-v3SwapGray">
                <BsCheckCircle
                  className="mr-1.5"
                  fill="#00D6AF"
                  stroke="#00D6AF"
                />

                <FormattedMessage
                  id="claimed"
                  defaultMessage={'Claimed'}
                ></FormattedMessage>
              </div>

              <div className="flex items-center justify-end">
                <span
                  title={toPrecision(claimedAmountIn, sellToken.decimals)}
                  className="text-v3SwapGray"
                >
                  {Number(claimedAmountIn) > 0 && Number(claimedAmountIn) < 0.01
                    ? '< 0.01'
                    : toPrecision(claimedAmountIn, 2)}
                </span>

                <span className="ml-1.5">{toRealSymbol(sellToken.symbol)}</span>
                <span className="mx-6 xs:mx-2 text-v3SwapGray">
                  {isClientMobile() ? (
                    <MyOrderInstantSwapArrowRight />
                  ) : (
                    <MyOrderInstantSwapArrowRight />
                  )}
                </span>
                <span title={claimedAmount} className="text-v3SwapGray">
                  {Number(claimedAmount) > 0 && Number(claimedAmount) < 0.01
                    ? '< 0.01'
                    : toPrecision(claimedAmount, 2)}
                </span>

                <span className="ml-1.5">{toRealSymbol(buyToken.symbol)}</span>
              </div>
            </span>
          </div>
        )}
      </td>
    </>
  );

  return (
    <Fragment>
      <tr>
        <td>
          <div className="pb-2.5"></div>
        </td>
      </tr>
      <tr
        className={`mb-4 overflow-visible xs:hidden px-4 py-3 text-sm w-full  items-center ${
          hoverOn === index
            ? 'bg-portfolioBarBgColor rounded-t-xl'
            : 'bg-portfolioBgColor rounded-xl'
        }`}
        onMouseEnter={() => {
          setHoverOn(index);
        }}
        style={{
          zIndex: 21,
        }}
      >
        <td
          className={
            hoverOn === index &&
            (new Big(order.original_deposit_amount || '0')
              .minus(order.original_amount || '0')
              .gt(0) ||
              Number(claimedAmountIn) > 0)
              ? ' rounded-tl-xl'
              : ' rounded-l-xl'
          }
        >
          {sellTokenAmount}
        </td>

        <td>
          <span className="text-white text-lg frcs w-7 xs:hidden ">
            <MyOrderInstantSwapArrowRight />
          </span>
        </td>

        <td>{buyTokenAmount}</td>

        <td>{feeTier}</td>

        <td>{orderRate}</td>

        <td>{created}</td>

        <td className="">{claimed}</td>
        <td
          className={
            hoverOn === index &&
            (new Big(order.original_deposit_amount || '0')
              .minus(order.original_amount || '0')
              .gt(0) ||
              Number(claimedAmountIn) > 0)
              ? ' rounded-tr-xl'
              : ' rounded-r-xl'
          }
        >
          {actions}
        </td>
      </tr>

      {hoverOn === index &&
        (new Big(order.original_deposit_amount || '0')
          .minus(order.original_amount || '0')
          .gt(0) ||
          Number(claimedAmountIn) > 0) && (
          <>
            <tr className="xs:flex z-20 relative  xs:flex-col whitespace-nowrap xs:bg-cardBg xs:bg-opacity-50  bottom-2 xs:bottom-0 w-full text-sm text-v3SwapGray bg-cardBg rounded-xl px-5 pb-5 pt-10 xs:px-3 xs:py-4 xs:text-xs">
              {swapBanner}
            </tr>
          </>
        )}
      <div
        className="w-full relative mb-4 md:hidden lg:hidden"
        style={{
          zIndex: 20 - index,
        }}
      >
        <MobileHistoryOrderStamp
          state={
            ONLY_ZEROS.test(order.cancel_amount)
              ? 'filled'
              : new Big(order.original_deposit_amount).eq(order.cancel_amount)
              ? 'cancel'
              : 'partially_filled'
          }
        />

        {/* title */}
        <div className="rounded-t-xl relative bg-orderMobileTop px-3 pt-3">
          <div className="absolute right-4 bottom-2.5 z-50  text-xs">
            <div className="relative">
              {!!orderTx && (
                <a
                  className="flex items-center text-v3SwapGray cursor-pointer"
                  onMouseEnter={() => handleMouseEnter(orderTx)}
                  onMouseLeave={handleMouseLeave}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  {loadingStates[orderTx] ? (
                    <>
                      Tx
                      <span className="loading-dots"></span>
                    </>
                  ) : (
                    <>
                      Tx
                      <span className="ml-1.5">
                        <HiOutlineExternalLink />
                      </span>
                    </>
                  )}
                  {hoveredTx === orderTx && (
                    <div className="w-44 absolute top-6 right-0 bg-poolDetaileTxBgColor border border-poolDetaileTxBorderColor rounded-lg p-2 shadow-lg z-50">
                      <div className="flex flex-col">
                        <div
                          className="mb-2 px-3 py-2 hover:bg-poolDetaileTxHoverColor text-white rounded-md flex items-center"
                          onMouseEnter={(event) => {
                            const arrow = event.currentTarget.querySelector(
                              '.arrow'
                            ) as HTMLElement;
                            if (arrow) {
                              arrow.style.display = 'block';
                            }
                          }}
                          onMouseLeave={(event) => {
                            const arrow = event.currentTarget.querySelector(
                              '.arrow'
                            ) as HTMLElement;
                            if (arrow) {
                              arrow.style.display = 'none';
                            }
                          }}
                          onClick={() =>
                            handleTxClick(
                              orderTx,
                              `${getConfig().explorerUrl}/txns`
                            )
                          }
                        >
                          <NearblocksIcon />
                          <p className="ml-2 text-sm">nearblocks</p>
                          <div
                            className="ml-3 arrow"
                            style={{ display: 'none' }}
                          >
                            <TxLeftArrow />
                          </div>
                        </div>
                        <div
                          className="px-3 py-2 hover:bg-poolDetaileTxHoverColor text-white rounded-md flex items-center"
                          onMouseEnter={(event) => {
                            const arrow = event.currentTarget.querySelector(
                              '.arrow'
                            ) as HTMLElement;
                            if (arrow) {
                              arrow.style.display = 'block';
                            }
                          }}
                          onMouseLeave={(event) => {
                            const arrow = event.currentTarget.querySelector(
                              '.arrow'
                            ) as HTMLElement;
                            if (arrow) {
                              arrow.style.display = 'none';
                            }
                          }}
                          onClick={() =>
                            handleTxClick(
                              orderTx,
                              `${getConfig().pikespeakUrl}/transaction-viewer`
                            )
                          }
                        >
                          <PikespeakIcon />
                          <p className="ml-2 text-sm">Pikespeak...</p>
                          <div
                            className="ml-3 arrow"
                            style={{ display: 'none' }}
                          >
                            <TxLeftArrow />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </a>
              )}
            </div>
          </div>

          <div className="flex items-center relative justify-between">
            {sellTokenAmount}
            <MyOrderMobileArrow />
            {buyTokenAmount}
          </div>

          {created}
        </div>
        {/*  content */}
        <div className="rounded-b-xl p-3 bg-cardBg">
          <MobileInfoBanner
            text={
              <FormattedMessage id="fee_tiers" defaultMessage={'Fee Tiers'} />
            }
            value={feeTier}
          />

          <MobileInfoBanner
            text={`1 ${toRealSymbol(
              sort ? buyToken?.symbol : tokensMap[order.sell_token].symbol
            )} Price`}
            value={orderRate}
          />

          <MobileInfoBanner
            text={
              <FormattedMessage defaultMessage={'Claimed'} id="claimed_upper" />
            }
            value={claimed}
          />

          {claimTip}
        </div>

        {/* swap banner */}
        {!ONLY_ZEROS.test(swapIn || '0') ? swapBanner : null}
      </div>
    </Fragment>
  );
}
